import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import ShowThumbnail from "@/app/admin/_common/ShowThumbnail";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import { cn } from "@/app/_utils/cn";
import { dayjsTz } from "@/app/_utils/dayjs";
import type { LocationShowRow } from "@/app/_services/LocationService";
import { Drama } from "lucide-react";

const currencyFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export default function LocationShowsList({ shows }: { shows: LocationShowRow[] }) {
  const navigate = useNavigate();

  return (
    <div className="flex h-full flex-col overflow-hidden border-t border-border bg-card md:rounded-md md:border md:border-border">
      <Table
        className={cn("min-w-[560px] table-fixed", shows.length === 0 && "h-full")}
        containerClassName="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
      >
        <TableHeader>
          <TableRow className="sticky top-0 z-10 border-b border-border hover:bg-muted/50 [&>th]:bg-background">
            <TableHead className="h-8 w-[34%] text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Show
            </TableHead>
            <TableHead className="h-8 w-[22%] text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Sold / Capacity
            </TableHead>
            <TableHead className="h-8 w-[18%] text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Revenue
            </TableHead>
            <TableHead className="h-8 w-[26%] text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Date
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {shows.length === 0 ? (
            <TableRow className="h-full hover:bg-transparent">
              <TableCell colSpan={4} className="h-full p-0">
                <CuoEmpty icon={Drama} text="No shows at this location yet" />
              </TableCell>
            </TableRow>
          ) : (
            shows.map((show) => (
            <TableRow
              key={show.id}
              className="cursor-pointer border-border/50"
              onClick={() => navigate(`/admin/shows/${show.id}/details`)}
            >
              <TableCell className="py-2">
                <div className="flex min-w-0 items-center gap-2.5">
                  <ShowThumbnail
                    src={show.img_url}
                    alt="show"
                    style={{ height: 32, width: 32, borderRadius: 6, flexShrink: 0 }}
                  />
                  <div className="min-w-0 leading-tight">
                    <div className="truncate text-[13.5px] text-foreground">
                      {show.title || "N/A"}
                    </div>
                    {show.variant_name && (
                      <div className="truncate text-[12px] text-muted-foreground">
                        {show.variant_name}
                      </div>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell className="py-2 text-[12.5px] text-muted-foreground">
                {show.sold} / {show.capacity}
              </TableCell>
              <TableCell className="whitespace-nowrap py-2">
                {currencyFormat.format(show.revenue)}
              </TableCell>
              <TableCell className="whitespace-nowrap py-2 text-right text-muted-foreground">
                {dayjsTz(show.start).format("MMM D, YYYY")}
              </TableCell>
            </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
